import React, {Component} from 'react';
import {findDOMNode} from 'react-dom';
import Immutable from 'immutable';
import {autobind} from 'core-decorators';
import classNames from 'classnames';
import {isMobile, getViewportHeight, getViewportWidth} from 'utils';
import {defaults as defaultOptions} from 'store/config/reducer';
import * as options from './options';
import {getConfigFromQueryString, parseConfigUpdates, getEmbedCode, getJsonLD, getShareLink} from './utils';
import Video from './video';
import ErrorMessage from './error';
import Toolbar from './toolbar';
import MockContent from './mock-content';

const CONFIG_DEFAULTS = {
    config: {
        // Config
        ...defaultOptions.merge({
            printEnabled: true,
            shareEnabled: true,
            downloadEnabled: true,
        }).toJS(),

        // Fastest Cars
        mdpsVersion: 3,
        jobId: '76bace73fb844a82bf5d04065abe8378',
        // videoId: 'GlOETXrU-wQ',  Video no longer exists
        videoId: '49ZCtLUdbso',

        // Universe
        // mdpsVersion: 1,
        // videoId: 'kR9imb7BSGU',
        // shortCode: '1t2dzds',

        videoSize: 560,
        videoProvider: 'youtube',
        panoptoHost: '',

        // SEO
        includeSeo: false,

        // Preview
        displayMockContent: false,
        domContainerEnabled: false,
        bodyBackground: options.bodyBackgroundOptions.getIn([0, 'value']),
        containerWidth: options.containerWidthOptions.getIn([0, 'value']),
        containerHeight: options.containerHeightOptions.getIn([1, 'value']),
    },
};

@autobind
export default class TestPage extends Component {
    constructor(props) {
        super(props);

        this.configOverrides = {};
        this.videoMetadata = null;

        this.state = {
            error: false,
            readOnly: false,
            disableEmbed: false,
            disableLink: false,
            toolbarOpened: !isMobile(),
            ...CONFIG_DEFAULTS,
        };
    }

    componentWillMount() {
        const [qsConfig, error] = getConfigFromQueryString(window.location.search, CONFIG_DEFAULTS);

        if (!qsConfig && !error) return;
        if (error) return this.setState({error: true});

        const {readOnly, disableLink, disableEmbed, ...configOverrides} = qsConfig;
        this.configOverrides = configOverrides;

        return this.setState({
            readOnly,
            disableLink,
            disableEmbed,
            config: {...this.state.config, ...configOverrides}},
        );
    }

    componentDidMount() {
        this.node = findDOMNode(this);
        this.updateContainerSize();
    }

    shouldComponentUpdate(nextProps, nextState) {
        return nextState.config !== this.state.config
            || nextState.error !== this.state.error
            || nextState.readOnly !== this.state.readOnly
            || nextState.toolbarOpened !== this.state.toolbarOpened
            || nextState.disableLink !== this.state.disableLink
            || nextState.disableEmbed !== this.state.disableEmbed;
    }

    componentDidUpdate() {
        this.updateContainerSize();
    }

    updateContainerSize() {
        const {domContainerEnabled, containerWidth, containerHeight} = this.state.config;
        if (domContainerEnabled && isMobile()) {
            const videoNode = this.node.querySelector('.video');

            const mobileWidth = '100%';
            const mobileHeight = getViewportHeight() - videoNode.clientHeight;

            if (mobileWidth !== containerWidth || mobileHeight !== containerHeight) {
                this.setState({
                    config: {
                        ...this.state.config,
                        containerWidth: mobileWidth,
                        containerHeight: mobileHeight,
                    },
                });
            }
        }
    }

    setTextareaRef(ref) {
        this.textarea = ref;
    }

    getPreviewKey(state) {
        return [
            JSON.stringify(this.getEmbedOptionsForPreview(state)),
            state.config.videoId,
            state.config.videoSize,
            state.config.videoProvider,
            state.config.panoptoHost,
            state.config.expandHeight,
            state.config.containerWidth,
        ].join('');
    }

    getEmbedOptions(state) {
        const {videoProvider, domContainerEnabled, domContainer} = state.config;

        return Immutable.fromJS({
            ...state.config,
            provider: videoProvider,
            domContainer: domContainerEnabled ? domContainer : '',
        });
    }

    getEmbedOptionsForPreview(state) {
        let embedOptions = this.getEmbedOptions(state);
        // Override domContainer option so that we use a known selector
        if (embedOptions.get('domContainerEnabled')) embedOptions = embedOptions.set('domContainer', '.dom-container');

        return embedOptions;
    }

    async copyCode(type) {
        if (type === 'shareLink') {
            const shareLink = await getShareLink(this.state.config);
            return this.copyToClipboard(shareLink);
        }

        if (type === 'embedCode') {
            const {includeSeo, onlySeo} = this.state.config;

            const embedOptions = this.getEmbedOptions(this.state);
            const parts = [getEmbedCode(embedOptions, !onlySeo)];
            if (this.videoMetadata && includeSeo) parts.push(getJsonLD(this.videoMetadata, !onlySeo));

            return this.copyToClipboard(parts.join('\n'));
        }
    }

    copyToClipboard(value) {
        this.textarea.value = value;
        this.textarea.select();
        document.execCommand('copy');
        this.textarea.value = '';
    }

    getConfigValues() {
        return this.state.config;
    }

    setVideoMetadata(metadata) {
        this.videoMetadata = metadata;
    }

    handleValueChange(payload) {
        this.setState({config: {...this.state.config, ...payload}});
    }

    handleConfigChange(config) {
        const updates = parseConfigUpdates(config, this.state.config).toJS();
        this.handleValueChange(updates);
    }

    handleConfigReset() {
        this.handleValueChange({...CONFIG_DEFAULTS.config, ...this.configOverrides});
    }

    handleToolbarToggle() {
        this.setState({toolbarOpened: !this.state.toolbarOpened});
    }

    render() {
        const mobile = isMobile();

        if (this.state.error) return <div className='test-page'><ErrorMessage /></div>;

        let videoWidth = this.state.config.videoSize;
        if (mobile) videoWidth = this.node ? this.node.clientWidth : getViewportWidth();

        const previewKey = this.getPreviewKey(this.state);
        const videoComponent = (
            <Video
                key={`${previewKey}-video`}
                id={this.state.config.videoId}
                provider={this.state.config.videoProvider}
                panopto_host={this.state.config.panoptoHost}
                accountId={this.state.config.accountId}
                options={this.getEmbedOptionsForPreview(this.state)}
                onVideoWrapped={this.setVideoMetadata}
                width={videoWidth}
                formats={this.state.config.videoFormats}
                metadata={this.state.config.videoMetadata}
            />
        );

        const containerComponent = this.state.config.domContainerEnabled && (
            <div
                key={`${previewKey}-container`}
                style={{width: this.state.config.containerWidth, height: this.state.config.containerHeight}}
                className='dom-container'
            />
        );

        const previewClassName = classNames('preview', {
            'toolbar-opened': this.state.toolbarOpened,
            'dom-container-visible': this.state.config.domContainerEnabled,
        });

        return (
            <div className='test-page'>
                <div className={previewClassName} style={{background: this.state.config.bodyBackground}}>
                    {this.state.config.displayMockContent
                        ? (
                            <MockContent
                                video={videoComponent}
                                mobile={mobile}
                                videoSize={this.state.config.videoSize}
                                domContainer={containerComponent}
                                videoSizeOptions={options.videoSizeOptions}
                                domContainerSize={this.state.config.containerWidth}
                            />
                        ) : (
                            <div className='columns'>
                                {videoComponent}
                                {containerComponent}
                            </div>
                        )
                    }
                    {!this.state.readOnly && mobile && (
                        <div className='mobile-toolbar-toggle' onClick={this.handleToolbarToggle}>
                            <i className='cielo24-vwrap-icon cielo24-vwrap-icon-chevron-left' />
                            <span>Open Toolbar</span>
                        </div>
                    )}
                </div>
                {!this.state.readOnly && (
                    <Toolbar
                        {...this.state}
                        mobile={mobile}
                        onToggle={this.handleToolbarToggle}
                        onValueChange={this.handleValueChange}
                        getConfigValues={this.getConfigValues}
                        onConfigChange={this.handleConfigChange}
                        onConfigReset={this.handleConfigReset}
                        onCodeCopy={this.copyCode}
                    />
                )}
                <textarea className='copy' ref={this.setTextareaRef} />
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./scripts/test-page/test-page.js